import {
  Activity,
  ArrowUpRight,
  Banknote,
  BookOpen,
  Boxes,
  BriefcaseBusiness,
  Calculator,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  ChartColumn,
  ChartNoAxesCombined,
  ClipboardList,
  Clock,
  DoorOpen,
  Dumbbell,
  GraduationCap,
  HardHat,
  Inbox,
  Landmark,
  LayoutDashboard,
  ListChecks,
  ListPlus,
  LockKeyhole,
  MapPin,
  Monitor,
  PhoneCall,
  Presentation,
  ReceiptText,
  ScanFace,
  ScanLine,
  ScrollText,
  Settings,
  Smartphone,
  ContactRound,
  Building2,
  ShieldCheck,
  ShieldX,
  Tags,
  Ticket,
  UserCheck,
  UserPlus,
  Users,
  UsersRound,
  Wallet,
  Whistle,
  type LucideIcon,
} from "lucide-react";
import type { PersonModuleClaimsDto, PlanFeature, UsageDto, VisitorPersonDto } from "@/shared/api/types";
import type { PermissionModule } from "@/shared/auth/permissions";
import { ROUTES } from "./routes";

export interface NavItem {
  id: string;
  label: string;
  to: string;
  icon: LucideIcon;
  /** Role gate (rules/01 §10); null = visible to every member. */
  permission: { module: PermissionModule; action: string } | null;
  /** Plan gate: when the entitlements say this feature is off, the entry stays visible but disabled with a reason. */
  planFeature?: PlanFeature;
  /** Shown but not navigable, with the reason (a feature that is not built yet). */
  disabledReason?: string;
  /**
   * The entry leads to a FAMILY rather than a page: the sidebar draws a chevron and opens these on
   * hover or focus (pass 07: "Reports becomes a flyout listing the six questions").
   *
   * They live here rather than being read out of the reports module because the shell may not import
   * a module (`tests/guards/imports`) — and because a nav that asks a module what is in it is a nav
   * that cannot be rendered until that module is loaded.
   */
  family?: readonly NavFamilyItem[];
}

/** One leaf of a nav family: what it is called, the question it answers, and where it goes. */
export interface NavFamilyItem {
  id: string;
  label: string;
  /** What the reader came to ask — the reason this is worth opening, in their words not ours. */
  question: string;
  to: string;
  icon: LucideIcon;
}

/** The reason an entry is disabled: a static one from the config, or the plan gate once entitlements are known. */
export function navDisabledReason(item: NavItem, usage: UsageDto | undefined): string | undefined {
  if (item.disabledReason) return item.disabledReason;
  if (item.planFeature && usage && !usage.features[item.planFeature]) {
    // M11: features are add-ons the customer can buy or trial themselves (ADR-0015 §2), so the way out is
    // a page in the portal with a price on it, not an email to Nexborg.
    return "An add-on you have not bought — see Settings → Subscription";
  }
  return undefined;
}

/** The ONE nav config; the sidebar is generated from it and an inventory test locks it to the catalog. */
export const NAV_ITEMS: readonly NavItem[] = [
  {
    id: "overview",
    label: "Overview",
    to: ROUTES.overview,
    icon: LayoutDashboard,
    permission: { module: "overview", action: "view" },
  },
  {
    id: "events",
    label: "Events",
    to: ROUTES.events,
    icon: ScrollText,
    permission: { module: "events", action: "view" },
  },
  {
    id: "devices",
    label: "Devices",
    to: ROUTES.devices,
    icon: ScanFace,
    permission: { module: "devices", action: "view" },
  },
  {
    id: "people",
    label: "People",
    to: ROUTES.people,
    icon: Users,
    permission: { module: "people", action: "view" },
  },
  {
    id: "doors",
    label: "Doors",
    to: ROUTES.doors,
    icon: DoorOpen,
    permission: { module: "doors", action: "view" },
  },
  {
    // The slot the design system reserved in M0 and M9 §4 finally fills (design-system §4). Since M12
    // it lands on the hub rather than on one report: there are six of them now. The core hub carries
    // only the core reports (ADR-0017 addendum 2026-09-15) — a module's own reports live in that
    // module's own workspace, below.
    id: "reports",
    label: "Reports",
    to: ROUTES.reports,
    icon: CalendarClock,
    permission: { module: "reports", action: "view" },
    // Every one of the six needs only the `reports:view` this entry already gates, which is exactly
    // why the hub had nothing left to decide and became this list (ADR-0017 addendum, pass 07).
    family: [
      {
        id: "attendance",
        label: "Attendance",
        question: "Who came in when, day by day, for a whole month.",
        to: ROUTES.reportsAttendance,
        icon: CalendarClock,
      },
      {
        id: "attendance-summary",
        label: "Attendance summary",
        question: "The month on one line per person: present, absent, late, hours.",
        to: ROUTES.reportsSummary,
        icon: CalendarCheck,
      },
      {
        id: "muster",
        label: "Daily muster",
        question: "Who is in and who is not, for one day at a time.",
        to: ROUTES.reportsMuster,
        icon: ClipboardList,
      },
      {
        id: "terminals",
        label: "Terminal activity",
        question: "How busy each terminal was, per day, and when its peak was.",
        to: ROUTES.reportsTerminals,
        icon: Activity,
      },
      {
        id: "denied",
        label: "Denied access",
        question: "Who was refused, at which terminal, why, and how often.",
        to: ROUTES.reportsDenied,
        icon: ShieldX,
      },
      {
        id: "enrolment",
        label: "Enrolment status",
        question: "Who has a face, cards and a PIN, and which terminals hold them.",
        to: ROUTES.reportsEnrolment,
        icon: UserCheck,
      },
    ],
  },
  {
    // M9 §5: who may be where, and when. Read by anyone who reads a person's terminal card.
    id: "schedules",
    label: "Schedules",
    to: ROUTES.schedules,
    icon: CalendarRange,
    permission: { module: "schedules", action: "view" },
  },
  {
    id: "help",
    label: "Guides & workflows",
    to: ROUTES.help,
    icon: BookOpen,
    permission: { module: "overview", action: "view" },
  },
  {
    id: "settings",
    label: "Settings",
    to: ROUTES.settings,
    icon: Settings,
    permission: { module: "settings", action: "view" },
  },
  {
    // The setup guide (setup-guides plan, lane A): the walk from an empty organisation to the first entry at a
    // door. The NINTH core entry, last, after Settings — Kumar's answer to README §10 question 2 of 2026-10-01
    // (~23:30): the sidebar entry as well as the Overview redirect, as Staff has had since 22:55. It is not "a
    // module folded into the core": no add-on unlocks it and it belongs to no module — it is the core's own
    // walk through the product every subscriber gets, and it stays one entry however many modules are bought
    // (each module's own guide lives in its own workspace). Gated `overview:view`, the core's "every member"
    // gate: without `settings:manage` the guide is a read-only checklist. Not organisation-level
    // (`ORG_LEVEL_ITEM_IDS`), so it does not follow the reader into a module. Its address lights THIS entry
    // and never Overview's — `/setup` is not under `/overview`.
    id: "setup",
    label: "Setup guide",
    to: ROUTES.setup,
    icon: ListChecks,
    permission: { module: "overview", action: "view" },
  },
];

/**
 * Organisation-level, not product-level (ADR-0017 second addendum): this follows the user into every
 * workspace, because "what we have bought" is a question about the organisation rather than about the
 * product they happen to be using.
 *
 * ONE entry since the 2026-09 redesign, where Members & roles became Settings tabs: billing, members,
 * roles, integrations and the audit log are all questions about the organisation, and Settings is
 * where the organisation is answered. One definition, rendered twice.
 */
export const ORG_LEVEL_ITEM_IDS: readonly string[] = ["settings"];

/**
 * A workspace is one product's menu (ADR-0017 second addendum, 2026-09-15). The sidebar shows **one at
 * a time** — the app-switcher pattern Odoo, Zoho One and the Microsoft/Google launchers converge on —
 * because the user is *in* one product at a time and six modules' worth of groups is a sidebar nobody
 * can scan. It replaces the one-section-per-module shape of the first addendum, which was right about
 * ownership and wrong about presentation.
 *
 * Core is the default and is what every subscriber gets; a module workspace is revealed by its own
 * add-on and never otherwise — a tenant with no add-ons never sees the switcher at all, so it cannot
 * become a standing advertisement for what they have not bought.
 *
 * Today only Gym has pages. The next five (Student Attendance, Canteen, Co-working, Rooms, Staff) each
 * add one entry to this list, keyed by their own `planFeature`, when their first page ships — the
 * shell does not change and no module (including this one) is special-cased in it.
 */
export interface Workspace {
  id: string;
  /**
   * What the switcher calls it: the business it serves, never the code it shares. It is the whole
   * tile — a launcher names products and does not explain them, and a module whose name needs a
   * sentence is a module with the wrong name.
   */
  label: string;
  icon: LucideIcon;
  /**
   * The URL prefix that selects this workspace. Core has none: it is the answer for every path that
   * no module claims, which is what makes a deep link, a refresh and the back button all work with no
   * stored state at all.
   */
  basePath?: string;
  /** The entitlement flag that reveals the whole workspace — the module's add-on, not a permission. */
  planFeature?: PlanFeature;
  /**
   * The workspace comes WITH the base plan rather than being sold beside it (M24: Face Kiosk is part of
   * Access Control — contracts §3, "no new item"). Its `planFeature` still reveals it, but nothing may treat
   * it as an add-on: no "add Face Kiosk" banner, no trial clock, no price (`useTenantBannerStates`).
   */
  baseIncluded?: boolean;
  items: readonly NavItem[];
  /**
   * The other direction of the relationship (ADR-0017 second addendum): "the module knows which
   * person it is about; the person page knows which modules claim them". `personClaims` is the ONE
   * read that answers it — `GET /<module>/people/{personId}` — and `personHref` is where the person
   * page's link out goes. A workspace without both draws nothing on a core page, which is the
   * default and the rule; a module never gets a card there, only a line of links.
   */
  personClaims?: {
    path: (personId: string) => string;
    /**
     * Where the link goes. Handed the module's own answer as well, because not every module keys its
     * page on the person: Visitors links to the durable visitor named in its response.
     */
    href: (personId: string, claims: PersonModuleClaimsDto) => string;
    /** The permission a caller needs before the portal asks at all; the api enforces it too. */
    permission: { module: PermissionModule; action: string };
  };
}

export const CORE_WORKSPACE_ID = "core";

export const WORKSPACES: readonly Workspace[] = [
  {
    id: CORE_WORKSPACE_ID,
    label: "Access control",
    icon: ShieldCheck,
    items: NAV_ITEMS,
  },
  {
    id: "gym",
    label: "Gym",
    icon: Dumbbell,
    basePath: "/gym",
    planFeature: "gym",
    personClaims: {
      path: (personId) => `/gym/people/${personId}`,
      href: (personId) => ROUTES.gymMember(personId),
      permission: { module: "gym", action: "view" },
    },
    items: [
      {
        // M15 10.1: the owner's tiles, from `GET /gym/dashboard`.
        id: "gym-dashboard",
        label: "Dashboard",
        to: ROUTES.gym,
        icon: LayoutDashboard,
        permission: { module: "gym", action: "view" },
      },
      {
        // M17 (plan §6): the enquiries that have not become anybody yet. Second, between the
        // dashboard and the members, because that is the order the business runs in — somebody asks,
        // and then somebody joins — and because the page is a front desk's first job of the day.
        //
        // `gym:view` reads it and `gym:manage` writes it: no permission module of its own, for the
        // reasoning already written against the `gym` entry in the catalog. Everything gym does —
        // register, sell, freeze, cancel — is the front desk's one job, and a lead is that desk's job
        // before all the others.
        id: "gym-leads",
        label: "Leads",
        to: ROUTES.gymLeads,
        icon: PhoneCall,
        permission: { module: "gym", action: "view" },
      },
      {
        // ADR-0017 second addendum: the member page is where a person's membership, plan and ledger
        // account live — NOT cards on the core person page, which cannot express a person who is both
        // a gym client and a gym trainer. The mapping is made here, in the module's own menu.
        id: "gym-members",
        label: "Members",
        to: ROUTES.gymMembers,
        icon: Users,
        permission: { module: "gym", action: "view" },
      },
      {
        // The plan master (PLAN-001..003): name, duration, price, the access group it grants.
        id: "gym-plans",
        label: "Plans",
        to: ROUTES.gymPlans,
        icon: Tags,
        permission: { module: "gym", action: "view" },
      },
      {
        // TRN-001..003: no trainer master, an ordinary `people` row with a roster entry — this is
        // where one is put on it or taken off. A member's own trainer is assigned from the member's
        // page (`GymClientCard`), beside the rest of that relationship.
        id: "gym-trainers",
        label: "Trainers",
        to: ROUTES.gymTrainers,
        icon: Whistle,
        permission: { module: "gym", action: "view" },
      },
      {
        // M14 §4 (ADR-0019): the organisation charging its OWN people — gym dues; a canteen wallet and
        // a co-working plan will use the same ledger from their own workspaces. The dues list.
        id: "gym-ledger",
        label: "Ledger",
        to: ROUTES.gymLedger,
        icon: Wallet,
        permission: { module: "ledger", action: "view" },
      },
      {
        // M15 §1 row 10.2 plus the five ledger reports (M14 §4 wave 3b, ADR-0019 decision 9), on the
        // module's own hub — moved off the core Reports hub because none of them mean anything to a
        // tenant without this module and `assertModule` answers 402 for every one. Gated `ledger:view`
        // rather than `gym:view`, unchanged from the five-report hub: a role holding only the ledger
        // pages must still reach its reports through this one entry (`WorkspaceSwitcher.test.tsx`
        // "offers a module to a role that holds only its ledger pages"). A `gym:view`-only role sees
        // the two module reports by opening `/gym/reports` directly or from the dashboard, until nav
        // gates an item on more than one permission.
        id: "gym-reports",
        label: "Reports",
        to: ROUTES.gymReports,
        icon: CalendarClock,
        permission: { module: "ledger", action: "view" },
      },
      {
        // GYM-002, GYM-003: grace, the dues flag (never a door), the entry group.
        // Labelled "Gym settings" rather than "Settings" — the CORE "Settings" (organisation, billing,
        // webhooks) already follows the user into every workspace (`ORG_LEVEL_ITEM_IDS`), so the two
        // would otherwise sit side by side under the same word.
        id: "gym-settings",
        label: "Gym settings",
        to: ROUTES.gymSettings,
        icon: Settings,
        permission: { module: "gym", action: "view" },
      },
      {
        // The setup guide (setup-guides plan, lane B): the walk from an empty gym to the first check-in. Last,
        // after Gym settings, the way Staff's is. Gated `gym:view` like the workspace: without `gym:manage` it is
        // a read-only checklist. Its address lights THIS entry and never the Dashboard — `/gym/setup` is beneath
        // `/gym`, and the longest match wins.
        id: "gym-setup",
        label: "Setup guide",
        to: ROUTES.gymSetup,
        icon: ListChecks,
        permission: { module: "gym", action: "view" },
      },
    ],
  },
  {
    /**
     * M16 wave 2 (ADR-0021 decision 9): **Visitors, in its own workspace.** Never an entry on the core
     * sidebar and never a page on the Reports hub — the module-navigation rule, which
     * `tests/guards/nav.test.ts` enforces by reading the source of every core module.
     *
     * `personClaims` since 2026-09-29 (Kumar): the visitor's person row is transient — minted on
     * issue and soft-deleted when the pass expires (decision 9) — but WHILE it exists, the person page
     * without a way back to the visitor is the dead end. So the claim links to the DURABLE visitor
     * named in the response (`visitorId`), not to anything keyed on the person.
     */
    id: "visitors",
    label: "Visitors",
    icon: ContactRound,
    basePath: "/visitors",
    planFeature: "visitors",
    personClaims: {
      path: (personId) => `/visitors/people/${personId}`,
      href: (_personId, claims) => {
        const visitorId = (claims as Partial<VisitorPersonDto>).visitorId;
        return visitorId ? ROUTES.visitor(visitorId) : ROUTES.visitors;
      },
      permission: { module: "visitors", action: "view" },
    },
    items: [
      {
        // The durable records: who has been here, who hosts them, and who is holding a live pass
        // right now — which is what puts revoke one click away (ADR-0021's second addendum).
        id: "visitors-list",
        label: "Visitors",
        to: ROUTES.visitors,
        icon: ContactRound,
        permission: { module: "visitors", action: "view" },
      },
      {
        // The register: one row per visit, with "in the building now" as its own filter. The question
        // a front desk asks all day and the one a fire warden asks once.
        id: "visitors-visits",
        label: "Visits",
        to: ROUTES.visits,
        icon: UserPlus,
        permission: { module: "visitors", action: "view" },
      },
      {
        // M16 wave 4 (ADR-0021 open question 4): scans at the doors, live. Gated on `passes:view`
        // rather than `visitors:view`, and that is the honest gate: a pass is a shared primitive, so
        // a gym day pass and a meeting-room guest appear on this page too. It lives in the Visitors
        // workspace because that is where a front desk is already standing (the module-navigation
        // rule — never the core sidebar, never the Reports hub).
        id: "visitors-scans",
        label: "Live scans",
        to: ROUTES.visitorScans,
        icon: ScanLine,
        permission: { module: "passes", action: "view" },
      },
      {
        // Visitors → Settings (visitor pass link, 2026-10-18): whether the desk can text a visitor a link to their
        // QR, and the three choices about what that link shows. Labelled "Visitor settings", not "Settings", for the
        // reason `gym-settings` is: the CORE Settings follows the user into every workspace. Gated `visitors:view`
        // like the workspace; without `visitors:manage` the page is read-only. Before the Setup guide, which stays last.
        id: "visitors-settings",
        label: "Visitor settings",
        to: ROUTES.visitorsSettings,
        icon: Settings,
        permission: { module: "visitors", action: "view" },
      },
      {
        // The setup guide (setup-guides plan, lane C): the walk from an empty register to a visitor through the
        // door and out again — the first setup surface this module has. Last, after Live scans. Gated
        // `visitors:view` like the workspace, and NOT `passes:view` like its neighbour: the guide is about the
        // module, not about passes shared with the gym. Without `visitors:manage` it is a read-only checklist.
        // Its address lights THIS entry and never the register's — `/visitors/setup` is beneath `/visitors`.
        id: "visitors-setup",
        label: "Setup guide",
        to: ROUTES.visitorsSetup,
        icon: ListChecks,
        permission: { module: "visitors", action: "view" },
      },
    ],
  },
  {
    /**
     * M19 (contracts §11, ADR-0023): **Staff attendance, in its own workspace** — the employee master,
     * the roster, the daily engine's days, requests, approvals and the month close. Never an entry on
     * the core sidebar and never a card on the core Reports hub (the module-navigation rule, which
     * `tests/guards/nav.test.ts` enforces). The `staff` add-on reveals it; every entry is gated on the
     * `staff` permission module and nothing else.
     *
     * `personClaims` is the link back from the core person page ("Also in: Staff"): the api answers
     * `GET /staff/people/{personId}` with the relationships, and the link opens the employee page keyed
     * on the same person id — an employee IS a person, never a parallel identity (ADR-0017 decision 1).
     */
    id: "staff",
    label: "Staff",
    icon: BriefcaseBusiness,
    basePath: "/staff",
    planFeature: "staff",
    personClaims: {
      path: (personId) => `/staff/people/${personId}`,
      href: (personId) => ROUTES.staffEmployee(personId),
      permission: { module: "staff", action: "view" },
    },
    items: [
      {
        // HR's morning screen (C3): who is in, late, absent, on leave or waiting for a decision today,
        // each tile opening the list behind it (ADR-0022 decision 2), plus the setup gaps.
        // Named "Dashboard" on Kumar's word of 2026-10-01 22:55 (it was "Today"). The id stays `staff-today`:
        // it is a key, not a name, and nothing is gained by churning it.
        id: "staff-today",
        label: "Dashboard",
        to: ROUTES.staff,
        icon: LayoutDashboard,
        permission: { module: "staff", action: "view" },
      },
      {
        // The employee master (C1): the directory, and register / import / the employee's own page
        // beneath it — which is why the three of them light this one entry.
        id: "staff-employees",
        label: "Employees",
        to: ROUTES.staffEmployees,
        icon: Users,
        permission: { module: "staff", action: "view" },
      },
      {
        // The month grid and the day drawer (C3): every day explained, and corrected with a reason.
        id: "staff-attendance",
        label: "Attendance",
        to: ROUTES.staffAttendance,
        icon: CalendarCheck,
        permission: { module: "staff", action: "view" },
      },
      {
        // The month (D wave, contracts §13.5): presence rate, leave, late and overtime for the
        // organisation, a team or a department, each opening its drill-down at `/staff/mis/:metric` —
        // which this entry lights too, the longest match.
        id: "staff-mis",
        label: "Monthly MIS",
        to: ROUTES.staffMis,
        icon: ChartNoAxesCombined,
        permission: { module: "staff", action: "view" },
      },
      {
        // One inbox for everything waiting on a decision (C4). Gated `staff:approve`, the action the
        // inbox exists for; a team approver who holds no staff permission decides from the phone.
        id: "staff-approvals",
        label: "Approvals",
        to: ROUTES.staffApprovals,
        icon: Inbox,
        permission: { module: "staff", action: "approve" },
      },
      {
        // Leave, permission and on-duty: every request with its routing, and filing one on behalf (C4).
        id: "staff-requests",
        label: "Requests",
        to: ROUTES.staffRequests,
        icon: ClipboardList,
        permission: { module: "staff", action: "view" },
      },
      {
        // The phone's punches with their selfie and map link (C3) — evidence, never access events.
        id: "staff-mobile-punches",
        label: "Mobile punches",
        to: ROUTES.staffMobilePunches,
        icon: Smartphone,
        permission: { module: "staff", action: "view" },
      },
      {
        // Shifts, assignments, holidays & week-offs, overrides and comp-offs (C2). The entry's address
        // is the section's prefix, so each of the five pages lights it.
        id: "staff-roster",
        label: "Roster",
        to: ROUTES.staffRoster,
        icon: CalendarRange,
        permission: { module: "staff", action: "view" },
      },
      {
        // The month close (C5): summaries, adjustments, close and reopen, the payroll export.
        id: "staff-periods",
        label: "Periods",
        to: ROUTES.staffPeriods,
        icon: LockKeyhole,
        permission: { module: "staff", action: "view" },
      },
      {
        // The module's own reports hub (C5) — the staff reports belong here, never on the core hub.
        id: "staff-reports",
        label: "Reports",
        to: ROUTES.staffReports,
        icon: CalendarClock,
        permission: { module: "staff", action: "view" },
      },
      {
        // Labelled "Staff settings", not "Settings", for the reason `gym-settings` is: the CORE
        // Settings follows the user into every workspace (`ORG_LEVEL_ITEM_IDS`), and two entries under
        // one word would sit side by side. Gated `staff:manage` (contracts §11): every tab is a write.
        id: "staff-settings",
        label: "Staff settings",
        to: ROUTES.staffSettings,
        icon: Settings,
        permission: { module: "staff", action: "manage" },
      },
      {
        // The setup guide (lane G): the walk from an empty organisation to the first punch. Kumar first
        // answered "no sidebar entry" and changed it on 2026-10-01 22:55, after seeing the guide light
        // "Today" in the sidebar. Last in the list, after Staff settings. Gated `staff:view` like the
        // workspace: without `staff:manage` the guide is a read-only checklist (G-setup-guide §1). Its address
        // lights THIS entry and never the Dashboard — the longest match wins.
        id: "staff-setup",
        label: "Setup guide",
        to: ROUTES.staffSetup,
        icon: ListChecks,
        permission: { module: "staff", action: "view" },
      },
    ],
  },
  {
    /**
     * M20 (contracts §11, ADR-0025): **Student Attendance, in its own workspace** — students and their
     * guardians, groups on an academic year, timetables and the calendar, the day-wise register and
     * parent SMS. Never an entry on the core sidebar and never a card on the core Reports hub (the
     * module-navigation rule, `tests/guards/nav.test.ts`). The `student_attendance` add-on reveals it;
     * every entry is gated on the `students` permission module and nothing else. The organisation's SMS
     * balance is NOT here: it is core (ADR-0024) and lives in Settings → SMS.
     *
     * `personClaims`: a student IS a person (ADR-0025), so the core person page's "Also in: Students"
     * link opens the student page keyed on the same person id.
     */
    id: "students",
    label: "Students",
    icon: GraduationCap,
    basePath: "/students",
    planFeature: "studentAttendance",
    personClaims: {
      path: (personId) => `/students/people/${personId}`,
      href: (personId) => ROUTES.studentsStudent(personId),
      permission: { module: "students", action: "view" },
    },
    items: [
      {
        // The morning screen (C3): expected, present, pending, absent and late per group, the terminals.
        id: "students-today",
        label: "Today",
        to: ROUTES.students,
        icon: LayoutDashboard,
        permission: { module: "students", action: "view" },
      },
      {
        // The student master (C1): the directory, and register / import / the student's page beneath it.
        id: "students-students",
        label: "Students",
        to: ROUTES.studentsStudents,
        icon: Users,
        permission: { module: "students", action: "view" },
      },
      {
        // The monthly register grid and the day drawer (C3).
        id: "students-attendance",
        label: "Attendance",
        to: ROUTES.studentsAttendance,
        icon: CalendarCheck,
        permission: { module: "students", action: "view" },
      },
      {
        // Classes / sections / batches on an academic year (C2); a group page lights this entry too.
        id: "students-groups",
        label: "Groups",
        to: ROUTES.studentsGroups,
        icon: UsersRound,
        permission: { module: "students", action: "view" },
      },
      {
        id: "students-timetables",
        label: "Timetables",
        to: ROUTES.studentsTimetables,
        icon: Clock,
        permission: { module: "students", action: "view" },
      },
      {
        // Holidays and group special days (C2), two tabs on one page.
        id: "students-calendar",
        label: "Calendar",
        to: ROUTES.studentsCalendar,
        icon: CalendarDays,
        permission: { module: "students", action: "view" },
      },
      {
        // Transfers and the bulk promotion (C2): every action on the page is a write.
        id: "students-promotions",
        label: "Promotions",
        to: ROUTES.studentsPromotions,
        icon: ArrowUpRight,
        permission: { module: "students", action: "manage" },
      },
      {
        // The module's own reports hub (C5) — never the core hub.
        id: "students-reports",
        label: "Reports",
        to: ROUTES.studentsReports,
        icon: CalendarClock,
        permission: { module: "students", action: "view" },
      },
      {
        // "Student settings", not "Settings", for the reason `staff-settings` is. Gated `students:manage`
        // (contracts §11); the `sms` tab inside is gated `students:sms` by the page itself.
        id: "students-settings",
        label: "Student settings",
        to: ROUTES.studentsSettings,
        icon: Settings,
        permission: { module: "students", action: "manage" },
      },
      {
        // The setup guide (lane G): last in the list, gated `students:view` like the workspace — without
        // `students:manage` it is a read-only checklist. Its address lights THIS entry and never Today.
        id: "students-setup",
        label: "Setup guide",
        to: ROUTES.studentsSetup,
        icon: ListChecks,
        permission: { module: "students", action: "view" },
      },
    ],
  },
  {
    /**
     * M25 (contracts §15, ADR-0027): **Labour attendance, in its own workspace** — contractors and sites with
     * their terminals, the labourers (each a person with a labour profile), the day-by-day attendance computed
     * from punches with no shift, fifteen reports and the module's settings. Never an entry on the core sidebar
     * and never a card on the core Reports hub (the module-navigation rule, `tests/guards/nav.test.ts`). The
     * `labour_attendance` add-on reveals it; every entry is gated on the `labour` permission module and nothing
     * else. Writes are gated again inside the pages (`manage`, `correct`, `lock`) and by the member's labour scope.
     *
     * `personClaims`: a labourer IS a person (ADR-0017), so the core person page's "Also in: Labour" link opens
     * the labourer page keyed on the same person id.
     */
    id: "labour",
    label: "Labour",
    icon: HardHat,
    basePath: "/labour",
    planFeature: "labourAttendance",
    personClaims: {
      path: (personId) => `/labour/people/${personId}`,
      href: (personId) => ROUTES.labourLabourer(personId),
      permission: { module: "labour", action: "view" },
    },
    items: [
      {
        // The site-day screen: who is expected, present, short and absent, per site, contractor and trade.
        id: "labour-today",
        label: "Today",
        to: ROUTES.labour,
        icon: LayoutDashboard,
        permission: { module: "labour", action: "view" },
      },
      {
        // The labour master: the directory, and register / import / photo round / the labourer's page beneath it.
        id: "labour-labourers",
        label: "Labourers",
        to: ROUTES.labourLabourers,
        icon: Users,
        permission: { module: "labour", action: "view" },
      },
      {
        id: "labour-contractors",
        label: "Contractors",
        to: ROUTES.labourContractors,
        icon: Building2,
        permission: { module: "labour", action: "view" },
      },
      {
        // Sites with their terminals (Dahua and Face Kiosk seats) and roles.
        id: "labour-sites",
        label: "Sites",
        to: ROUTES.labourSites,
        icon: MapPin,
        permission: { module: "labour", action: "view" },
      },
      {
        // The grid and the day drawer; the exceptions list beneath it lights this entry too. A correction
        // inside is gated `labour:correct` by the page itself.
        id: "labour-attendance",
        label: "Attendance",
        to: ROUTES.labourAttendance,
        icon: CalendarCheck,
        permission: { module: "labour", action: "view" },
      },
      {
        // The module's own reports hub — never the core hub. Files leave on `labour:export` (the page's own gate).
        id: "labour-reports",
        label: "Reports",
        to: ROUTES.labourReports,
        icon: CalendarClock,
        permission: { module: "labour", action: "view" },
      },
      {
        // "Labour settings", not "Settings", for the reason `staff-settings` is. Gated `labour:view` (contracts
        // §15): a reader sees the rules; every write inside needs `labour:manage` and scope `all`, the lock `labour:lock`.
        id: "labour-settings",
        label: "Labour settings",
        to: ROUTES.labourSettings,
        icon: Settings,
        permission: { module: "labour", action: "view" },
      },
      {
        // The setup guide (lane G): last in the list, gated `labour:manage` (contracts §15) — the walk from an
        // empty organisation to the first computed day. Its address lights THIS entry and never Today.
        id: "labour-setup",
        label: "Setup guide",
        to: ROUTES.labourSetup,
        icon: ListChecks,
        permission: { module: "labour", action: "manage" },
      },
    ],
  },
  {
    /**
     * M26 (contracts §20, ADR-0031): **Payroll, in its own workspace** — the month's run and its payslips, the
     * employees' salary structures, statutory details and bank accounts, the month's inputs, income tax, payments,
     * four reports and the module's settings. Never an entry on the core sidebar and never a card on the core
     * Reports hub (the module-navigation rule, `tests/guards/nav.test.ts`). The `payroll` add-on reveals it, and only
     * while Staff is live (`features.payroll` is false otherwise — the api decides, ADR-0031 d2); every entry is
     * gated on the `payroll` permission module and nothing else. Writes are gated again inside the pages (`manage`,
     * `adjust`, `run`, `close`, `approve`, `pay`, `export`).
     *
     * `personClaims`: an employee IS a person (ADR-0017), so the core person page's "Also in: Payroll" link opens the
     * employee's payroll page keyed on the same person id (route 24 answers the claim).
     */
    id: "payroll",
    label: "Payroll",
    icon: Wallet,
    basePath: "/payroll",
    planFeature: "payroll",
    personClaims: {
      path: (personId) => `/payroll/people/${personId}`,
      href: (personId) => ROUTES.payrollEmployee(personId),
      permission: { module: "payroll", action: "view" },
    },
    items: [
      {
        // The runs list; a month's run, its payslip grid and a payslip's explain page beneath it light this entry.
        id: "payroll-runs",
        label: "Runs",
        to: ROUTES.payroll,
        icon: Calculator,
        permission: { module: "payroll", action: "view" },
      },
      {
        // Salary structures, statutory details and bank accounts; import and bulk structures beneath it.
        id: "payroll-employees",
        label: "Employees",
        to: ROUTES.payrollEmployees,
        icon: Users,
        permission: { module: "payroll", action: "view" },
      },
      {
        // The month's adjustments (overtime, loans, claims, arrears…); writes need `payroll:adjust`.
        id: "payroll-inputs",
        label: "Inputs",
        to: ROUTES.payrollInputs,
        icon: ListPlus,
        permission: { module: "payroll", action: "view" },
      },
      {
        // Declarations and proofs, tax settings and prior income; decisions need `payroll:approve`.
        id: "payroll-tax",
        label: "Tax",
        to: ROUTES.payrollTax,
        icon: Landmark,
        permission: { module: "payroll", action: "view" },
      },
      {
        // Bank file and recorded payments: `payroll:pay`, the permission that sees bank accounts.
        id: "payroll-payments",
        label: "Payments",
        to: ROUTES.payrollPayments,
        icon: Banknote,
        permission: { module: "payroll", action: "pay" },
      },
      {
        // The module's own reports hub — never the core hub. Files leave on `payroll:export` (the page's own gate).
        id: "payroll-reports",
        label: "Reports",
        to: ROUTES.payrollReports,
        icon: ChartColumn,
        permission: { module: "payroll", action: "view" },
      },
      {
        // "Payroll settings", not "Settings", for the reason `staff-settings` is. Writes inside need `manage`.
        id: "payroll-settings",
        label: "Payroll settings",
        to: ROUTES.payrollSettings,
        icon: Settings,
        permission: { module: "payroll", action: "view" },
      },
      {
        // The setup guide (lane G): last in the list, gated `payroll:manage` (contracts §20). Its address lights
        // THIS entry and never Runs.
        id: "payroll-setup",
        label: "Setup guide",
        to: ROUTES.payrollSetup,
        icon: ListChecks,
        permission: { module: "payroll", action: "manage" },
      },
    ],
  },
  {
    /**
     * M21 (contracts §15, ADR-0028, ADR-0029): **Co-working, in its own workspace** — companies and members, the
     * contracts that sell them seats, the operator's own invoices, notes, payments, deposits and TDS, the unpaid
     * door, day passes, products, six reports and the module's settings. Never an entry on the core sidebar and
     * never a card on the core Reports hub (the module-navigation rule, `tests/guards/nav.test.ts`). The
     * `coworking` add-on reveals it; every entry is gated on the `coworking` permission module and nothing else.
     * Writes are gated again inside the pages (`manage`, `invoicing`, `contracts`) and, for money, by `ledger:*`.
     * "Invoices" here is the operator's document, never Nexborg's (ADR-0019 addendum, item 3).
     *
     * **Invoices & payments** is ONE entry leading to a family (the flyout core's Reports uses): invoices, credit
     * notes, payments, deposits, TDS, statements and billing runs. Its pages light it (`activeNavItemId` reads a
     * family's addresses), though none of them sits under its own `to`.
     *
     * `personClaims`: a member IS a person (ADR-0017), so the core person page's "Also in: Co-working" link opens
     * the member page keyed on the same person id.
     */
    id: "coworking",
    label: "Co-working",
    icon: Building2,
    basePath: "/coworking",
    planFeature: "coworking",
    personClaims: {
      path: (personId) => `/coworking/people/${personId}`,
      href: (personId) => ROUTES.coworkingMember(personId),
      permission: { module: "coworking", action: "view" },
    },
    items: [
      {
        // The morning: thirteen tiles, the task lists, "n of 7" of the setup guide.
        id: "coworking-dashboard",
        label: "Dashboard",
        to: ROUTES.coworking,
        icon: LayoutDashboard,
        permission: { module: "coworking", action: "view" },
      },
      {
        // Companies are ledger parties with 1..n GST registrations (ADR-0028); the company page lights this entry.
        id: "coworking-companies",
        label: "Companies",
        to: ROUTES.coworkingCompanies,
        icon: Building2,
        permission: { module: "coworking", action: "view" },
      },
      {
        // The member directory, and register / the member's page beneath it.
        id: "coworking-members",
        label: "Members",
        to: ROUTES.coworkingMembers,
        icon: Users,
        permission: { module: "coworking", action: "view" },
      },
      {
        // Draft, sign, seats, notice and end; the contract page lights this entry too.
        id: "coworking-contracts",
        label: "Contracts",
        to: ROUTES.coworkingContracts,
        icon: ScrollText,
        permission: { module: "coworking", action: "view" },
      },
      {
        // ADR-0019 addendum, D4: "Invoices & payments", not "Billing" — Nexborg's own invoices stay under
        // Settings → Subscription. One row, a flyout of seven; the five ledger surfaces are Statements.
        id: "coworking-billing",
        label: "Invoices & payments",
        to: ROUTES.coworkingInvoices,
        icon: ReceiptText,
        permission: { module: "coworking", action: "view" },
        family: [
          {
            id: "invoices",
            label: "Invoices",
            question: "Every invoice you have issued, and what is still owed on it.",
            to: ROUTES.coworkingInvoices,
            icon: ReceiptText,
          },
          {
            id: "credit-notes",
            label: "Credit notes",
            question: "Corrections to invoices already issued.",
            to: ROUTES.coworkingCreditNotes,
            icon: ClipboardList,
          },
          {
            id: "payments",
            label: "Payments",
            question: "Money received, how it was split across invoices, and what was deducted as TDS.",
            to: ROUTES.coworkingPayments,
            icon: Banknote,
          },
          {
            id: "deposits",
            label: "Deposits",
            question: "What each contract holds, and what has been released, refunded or kept.",
            to: ROUTES.coworkingDeposits,
            icon: Landmark,
          },
          {
            id: "tds",
            label: "TDS",
            question: "Tax your customers deducted, and the certificates still to come.",
            to: ROUTES.coworkingTds,
            icon: Calculator,
          },
          {
            id: "statements",
            label: "Statements",
            question: "One company's or member's account, line by line.",
            to: ROUTES.coworkingStatements,
            icon: ScrollText,
          },
          {
            id: "billing-runs",
            label: "Billing runs",
            question: "Each day's invoice run: what it issued, and what it left out.",
            to: ROUTES.coworkingBilling,
            icon: CalendarClock,
          },
        ],
      },
      {
        // The unpaid door (ADR-0018): accounts that may be blocked, and the ones that are. Blocking is a click.
        id: "coworking-unpaid",
        label: "Ready to block",
        to: ROUTES.coworkingUnpaid,
        icon: ShieldX,
        permission: { module: "coworking", action: "view" },
      },
      {
        id: "coworking-day-passes",
        label: "Day passes",
        to: ROUTES.coworkingDayPasses,
        icon: Ticket,
        permission: { module: "coworking", action: "view" },
      },
      {
        id: "coworking-products",
        label: "Products",
        to: ROUTES.coworkingProducts,
        icon: Tags,
        permission: { module: "coworking", action: "view" },
      },
      {
        // The module's own reports hub — never the core hub. Files leave on `coworking:export` (the page's own gate).
        id: "coworking-reports",
        label: "Reports",
        to: ROUTES.coworkingReports,
        icon: ChartColumn,
        permission: { module: "coworking", action: "view" },
      },
      {
        // The three cut-over files: members, contracts, opening documents. Every action on the page is a write.
        id: "coworking-import",
        label: "Import",
        to: ROUTES.coworkingImport,
        icon: ListPlus,
        permission: { module: "coworking", action: "manage" },
      },
      {
        // "Co-working settings", not "Settings", for the reason `staff-settings` is: the CORE Settings follows the
        // reader into every workspace, and two entries under one word would sit side by side. Gated
        // `coworking:manage` (contracts §15); the seller and series tabs also need `coworking:invoicing`.
        id: "coworking-settings",
        label: "Co-working settings",
        to: ROUTES.coworkingSettings,
        icon: Settings,
        permission: { module: "coworking", action: "manage" },
      },
      {
        // The setup guide (lane G): last in the list, gated `coworking:view` like the workspace — without
        // `coworking:manage` it is a read-only checklist. Its address lights THIS entry and never the Dashboard.
        id: "coworking-setup",
        label: "Setup guide",
        to: ROUTES.coworkingSetup,
        icon: ListChecks,
        permission: { module: "coworking", action: "view" },
      },
    ],
  },
  {
    /**
     * M27 (contracts §18, ADR-0030 addendum 2026-10-28): **Rooms and Equipment, in its own workspace** — today's
     * bookings and the problems behind them, the booking calendar, the rooms and equipment with their hours, doors and
     * booker lists, three reports, the wall displays and the module's settings. Never an entry on the core sidebar
     * and never a card on the core Reports hub (the module-navigation rule, `tests/guards/nav.test.ts`). The `rooms`
     * add-on reveals it; every entry is gated on the `rooms` permission module and nothing else. Writes are gated
     * again inside the pages (`manage`, `book`, `cancel`) and by the member's device scope. The booking engine itself
     * has no screen. No `personClaims`: a person is booked into a room, not claimed by it.
     */
    id: "rooms",
    label: "Rooms and Equipment",
    icon: Presentation,
    basePath: "/rooms",
    planFeature: "rooms",
    items: [
      {
        // Today's bookings, the resources out of service and the upcoming bookings with an access problem.
        id: "rooms-overview",
        label: "Overview",
        to: ROUTES.roomsOverview,
        icon: LayoutDashboard,
        permission: { module: "rooms", action: "view" },
      },
      {
        // The calendar by default, the list behind `?view=list`; the booking's page and its form sit beneath it.
        id: "rooms-bookings",
        label: "Bookings",
        to: ROUTES.roomsBookings,
        icon: CalendarDays,
        permission: { module: "rooms", action: "view" },
      },
      {
        id: "rooms-resources",
        label: "Resources",
        to: ROUTES.roomsResources,
        icon: Boxes,
        permission: { module: "rooms", action: "view" },
      },
      {
        // The module's own reports hub — never the core hub. Files leave on `rooms:export` (the page's own gate).
        id: "rooms-reports",
        label: "Reports",
        to: ROUTES.roomsReports,
        icon: ChartColumn,
        permission: { module: "rooms", action: "view" },
      },
      {
        // Pairing codes and paired wall screens: every action on the page is a write.
        id: "rooms-displays",
        label: "Displays",
        to: ROUTES.roomsDisplays,
        icon: Monitor,
        permission: { module: "rooms", action: "manage" },
      },
      {
        // "Rooms settings", not "Settings", for the reason `staff-settings` is: the CORE Settings follows the
        // reader into every workspace, and two entries under one word would sit side by side.
        id: "rooms-settings",
        label: "Rooms settings",
        to: ROUTES.roomsSettings,
        icon: Settings,
        permission: { module: "rooms", action: "manage" },
      },
      {
        // The setup guide (lane G): last in the list, gated `rooms:view` like the workspace — without
        // `rooms:manage` it is a read-only checklist. Its address lights THIS entry and never Overview.
        id: "rooms-setup",
        label: "Setup guide",
        to: ROUTES.roomsSetup,
        icon: ListChecks,
        permission: { module: "rooms", action: "view" },
      },
    ],
  },
  {
    /**
     * M24 (contracts §11, ADR-0026): **Face Kiosk, in its own workspace** — seats (a phone at a gate in a
     * terminal slot), the scan log, approvals, consent coverage and its settings. Never an entry on the core
     * sidebar and never a card on the core Reports hub (the module-navigation rule). It comes with Access
     * Control (`baseIncluded`): `features.faceKiosk` is true wherever Access Control is live, and there is no
     * catalogue item to sell. No `personClaims`: the person page's face card is a shared core component (C2),
     * and no setup guide of its own (lane G extends the three existing ones).
     */
    id: "faceKiosk",
    label: "Face Kiosk",
    icon: ScanFace,
    basePath: "/face-kiosk",
    planFeature: "faceKiosk",
    baseIncluded: true,
    items: [
      {
        // The seats (C1): one per terminal slot given to a phone, its holder, its phone, today's scans.
        id: "face-kiosk-seats",
        label: "Seats",
        to: ROUTES.faceKiosk,
        icon: Smartphone,
        permission: { module: "face_kiosk", action: "view" },
      },
      {
        // The receipt log (C1); its export control is gated `face_kiosk:export` by the page itself.
        id: "face-kiosk-scans",
        label: "Scans",
        to: ROUTES.faceKioskScans(),
        icon: ScanLine,
        permission: { module: "face_kiosk", action: "view" },
      },
      {
        id: "face-kiosk-approvals",
        label: "Approvals",
        to: ROUTES.faceKioskApprovals(),
        icon: UserCheck,
        permission: { module: "face_kiosk", action: "approve" },
      },
      {
        // Coverage per list; confirming and withdrawing inside are gated `people:edit` by the page itself.
        id: "face-kiosk-consent",
        label: "Consent",
        to: ROUTES.faceKioskConsent(),
        icon: ClipboardList,
        permission: { module: "face_kiosk", action: "view" },
      },
      {
        // "Face Kiosk settings", not "Settings", for the reason `staff-settings` is.
        id: "face-kiosk-settings",
        label: "Face Kiosk settings",
        to: ROUTES.faceKioskSettings,
        icon: Settings,
        permission: { module: "face_kiosk", action: "manage" },
      },
    ],
  },
];

export const CORE_WORKSPACE: Workspace = WORKSPACES[0] as Workspace;

/**
 * Which workspace a URL is in. Pure, and the ONLY source of the answer: nothing is stored, so a deep
 * link selects the right workspace, a refresh keeps it, and the back button walks out of it — the
 * three things a remembered "current module" gets wrong. A path no module claims is Core, which is
 * also why Settings and Members & roles land a user back in Core: they are core surfaces.
 */
export function workspaceForPath(pathname: string): Workspace {
  for (const workspace of WORKSPACES) {
    const base = workspace.basePath;
    if (base && (pathname === base || pathname.startsWith(`${base}/`))) return workspace;
  }
  return CORE_WORKSPACE;
}

/** Where the switcher lands when a workspace is chosen: its first entry that is actually navigable. */
export function workspaceHome(workspace: Workspace, items: readonly NavItem[] = workspace.items): string {
  const first = items.find((item) => !item.disabledReason);
  return first?.to ?? workspace.basePath ?? ROUTES.overview;
}

/**
 * An entry's address as a path to compare: no query, no hash, no trailing slash.
 *
 * Two entries carry a query today — `ROUTES.schedulesTab("holidays")` and `ROUTES.settingsTab(…)` —
 * and a tab is a place WITHIN a page, never a menu entry of its own, so the query can only ever make a
 * match fail. The trailing slash is the same class of accident from the other direction: `/visitors/`
 * and `/visitors` are one page, and a reader who typed the slash must still see the menu light up.
 */
function navPath(to: string): string {
  const path = to.split(/[?#]/)[0] ?? to;
  return path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path;
}

/**
 * Whether `pathname` is AT or BENEATH `to`, counted in **path segments**. String containment is the
 * trap: `"/visitorsomething".startsWith("/visitors")` is true and that page is no relation of the
 * Visitors menu, so the only prefix that counts is one followed by a separator.
 */
function isBeneath(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`);
}

/**
 * Which ONE of these entries the current URL belongs to: of the entries whose path prefixes it, the
 * **longest** — the most specific description of where the reader actually is.
 *
 * This exists because `NavLink`'s own `isActive` answers a different question: "is the location at or
 * under my `to`", which is true of a parent and a child at once. On `/visitors/visits` both Visitors
 * (`/visitors`) and Visits (`/visitors/visits`) lit up, in every workspace, because the sidebar is one
 * shared component. Adding `end` to the links would swap the defect for its mirror image: a visitor
 * detail page at `/visitors/:id` is not any entry's `to`, and with `end` nothing at all would light up
 * — the reader's sense of place would vanish exactly where a deep page needs it most.
 *
 * Longest-match wins is the rule that satisfies both: `/visitors/visits` is Visits, `/visitors/abc123`
 * is Visitors, and no URL is ever described twice.
 *
 * The caller passes **every entry rendered in the sidebar at once** (both lists — see `Sidebar.tsx`),
 * because "most specific wins" is only true across the whole menu; resolved per list it would
 * degenerate into "at most one per list", which is two highlights again the first time an
 * organisation-level entry and a workspace entry nest.
 *
 * Returns `null` for a URL under no entry — a page reached from a link rather than the menu (a person,
 * a group, the kitchen sink). Nothing is highlighted then, which is the honest answer.
 */
export function activeNavItemId(pathname: string, items: readonly NavItem[]): string | null {
  const here = navPath(pathname);
  let bestId: string | null = null;
  let bestLength = -1;
  for (const item of items) {
    // M21: a family's pages light its parent. "Invoices & payments" owns `/coworking/payments` and its five
    // siblings, none of which sits under its own `to`; read alone, the longest match for them would be the
    // workspace's Dashboard (`/coworking`). Core's Reports family sits under its own `to`, so nothing there moves.
    for (const target of [item.to, ...(item.family ?? []).map((leaf) => leaf.to)]) {
      const to = navPath(target);
      if (!isBeneath(here, to) || to.length <= bestLength) continue;
      bestId = item.id;
      bestLength = to.length;
    }
  }
  return bestId;
}
